/**
 * The team you can build before you have a project.
 *
 * Team membership was only reachable from a project's workspace toolbar, and
 * that toolbar does not exist until the project clears discovery — so someone
 * could buy Team, pay for three seats, and have no way to invite anyone until
 * they had created and built something. This renders wherever the settings
 * panel does (dashboard and workspace alike), so the roster is reachable the
 * moment the plan is.
 *
 * An account invitation grants a SEAT — the teammate's own billing screen says
 * Team — and deliberately nothing else. Project access stays an explicit,
 * per-project decision, so growing a roster never silently widens who can read
 * the owner's code.
 */
import { useCallback, useEffect, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Input } from '@molecule/app-ui-react'

/** A person holding one of this account's seats. */
interface RosterMember {
  id: string
  email: string
  username?: string | null
  name?: string | null
}

/** An outstanding account invitation. */
interface AccountInvite {
  id: string
  email: string
  role: string
  expiresAt: string
}

/** What `GET /account/team` answers. */
interface AccountTeamData {
  seats: { total: number; used: number; perSeat: boolean }
  members: RosterMember[]
  invites: AccountInvite[]
}

/** The message an API rejection carries, if it has one. */
function errorMessageOf(error: unknown): string | null {
  const e = error as { response?: { data?: { error?: unknown } } } | null | undefined
  const message = e?.response?.data?.error
  return typeof message === 'string' && message ? message : null
}

/**
 * Account-level team roster: seats, members, invitations.
 *
 * @param props - The section props.
 * @param props.onUpgrade - Opens the upgrade flow. Plans that do not sell
 *   seats cannot invite (the API refuses it), so the empty state's CTA routes
 *   there instead of an invite form that can only fail.
 * @returns The section body, or null while the roster read is still in flight.
 */
export function AccountTeamSection({ onUpgrade }: { onUpgrade?: () => void }) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  const [data, setData] = useState<AccountTeamData | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await http.get<{ data: AccountTeamData }>('/account/team')
      setData(res.data?.data ?? null)
    } catch (_error) {
      setData(null)
    }
    setLoaded(true)
  }, [http])

  useEffect(() => {
    void load()
  }, [load])

  const invite = useCallback(async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await http.post('/account/team/invite', { email: email.trim() })
      setEmail('')
      setNotice(
        t('accountTeam.invited', undefined, {
          defaultValue: 'Invitation sent. They will see it when they sign in.',
        }),
      )
      await load()
    } catch (err) {
      setError(
        errorMessageOf(err) ??
          t('accountTeam.inviteFailed', undefined, {
            defaultValue: 'Could not send that invitation.',
          }),
      )
    }
    setBusy(false)
  }, [email, http, load, t])

  const cancelInvite = useCallback(
    async (id: string) => {
      await http.delete(`/account/team/invites/${id}`).catch(() => {})
      await load()
    },
    [http, load],
  )

  const removeMember = useCallback(
    async (id: string) => {
      await http.delete(`/account/team/members/${id}`).catch(() => {})
      await load()
    },
    [http, load],
  )

  // While the read is in flight there is nothing to say yet. Once it settles
  // the section always speaks: who is on the team (just the owner, until
  // someone accepts) and how to grow it — an empty "Team" card with no body
  // under the heading reads as a bug.
  if (!loaded) return null

  if (!data?.seats.perSeat) {
    // A plan that does not sell seats has no roster and the API refuses its
    // invites, so the CTA opens the upgrade flow (the Billing section's modal)
    // rather than an invite form that can only fail. It still names the empty
    // roster instead of going quiet.
    return (
      <div data-mol-id="account-team-empty">
        <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 12 }}>
          {t('accountTeam.empty', undefined, { defaultValue: 'Just you for now.' })}
        </div>
        {onUpgrade && (
          <Button
            size="sm"
            className={cm.touchTargetCompact}
            data-mol-id="account-team-invite-cta"
            onClick={onUpgrade}
          >
            {t('accountTeam.invitePeople', undefined, { defaultValue: 'Invite people' })}
          </Button>
        )}
      </div>
    )
  }

  return (
    <div data-mol-id="account-team">
      <div
        data-mol-id="account-team-seats"
        style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}
      >
        {t(
          'accountTeam.seatsInUse',
          { used: data.seats.used, total: data.seats.total },
          { defaultValue: '{{used}} of {{total}} seats in use' },
        )}
      </div>

      {data.members.length === 0 && data.invites.length === 0 && (
        <div
          data-mol-id="account-team-empty"
          style={{ fontSize: 13, opacity: 0.7, padding: '4px 0 8px' }}
        >
          {t('accountTeam.empty', undefined, { defaultValue: 'Just you for now.' })}
        </div>
      )}

      {data.members.map((member) => (
        <Flex
          key={member.id}
          align="center"
          justify="between"
          gap="xs"
          style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}
        >
          <span style={{ fontSize: 13 }} data-mol-id={`account-team-member-${member.id}`}>
            {member.name || member.username || member.email}
          </span>
          <Button
            size="sm"
            variant="outline"
            className={cm.touchTargetCompact}
            data-mol-id={`account-team-remove-${member.id}`}
            onClick={() => void removeMember(member.id)}
          >
            {t('accountTeam.remove', undefined, { defaultValue: 'Remove' })}
          </Button>
        </Flex>
      ))}

      {data.invites.map((pending) => (
        <Flex
          key={pending.id}
          align="center"
          justify="between"
          gap="xs"
          style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}
        >
          <span
            style={{ fontSize: 13, opacity: 0.75 }}
            data-mol-id={`account-team-invite-${pending.id}`}
          >
            {t(
              'accountTeam.pending',
              { email: pending.email },
              { defaultValue: '{{email}} — invited' },
            )}
          </span>
          <Button
            size="sm"
            variant="outline"
            className={cm.touchTargetCompact}
            data-mol-id={`account-team-invite-cancel-${pending.id}`}
            onClick={() => void cancelInvite(pending.id)}
          >
            {t('common.cancel', undefined, { defaultValue: 'Cancel' })}
          </Button>
        </Flex>
      ))}

      <Flex align="center" gap="sm" style={{ marginTop: 12, flexWrap: 'wrap' }}>
        <Input
          type="email"
          value={email}
          data-mol-id="account-team-invite-email"
          placeholder={t('accountTeam.emailPlaceholder', undefined, {
            defaultValue: 'teammate@example.com',
          })}
          onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
          // 16px on touch: iOS zooms the page for any field smaller.
          style={{ flex: '1 1 220px', fontSize: 16 }}
        />
        <Button
          size="sm"
          className={cm.touchTargetCompact}
          data-mol-id="account-team-invite-submit"
          disabled={busy || !email.trim()}
          onClick={() => void invite()}
        >
          {t('accountTeam.invite', undefined, { defaultValue: 'Invite' })}
        </Button>
      </Flex>

      {error && (
        <div
          data-mol-id="account-team-error"
          className={cm.textError}
          style={{ fontSize: 12, marginTop: 8 }}
        >
          {error}
        </div>
      )}
      {notice && (
        <div
          data-mol-id="account-team-notice"
          style={{ fontSize: 12, marginTop: 8, opacity: 0.75 }}
        >
          {notice}
        </div>
      )}
    </div>
  )
}
